import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { api } from "../lib/api";
import { Footer } from "../components/Footer";
import { LanguageToggle } from "../components/LanguageToggle";
import { LeaderboardAd } from "../components/AdSlot";
import { ISLAND_BY_SLUG } from "../lib/constants";
import { useSeoTags } from "../lib/useSeoTags";
import { t as translate } from "../lib/i18n";

// URL slug -> DB slug (matches App.js IslandRouteGuard).
// The `st-maarten` (dashed) form is kept as a backward-compat alias
// for any older external inbound links; the canonical form is
// `stmaarten` (no dash), matching /api/islands.
const URL_TO_SLUG = {
  aruba: "aruba",
  curacao: "curacao",
  bonaire: "bonaire",
  stmaarten: "stmaarten",
  "st-maarten": "stmaarten",
};

const ISLAND_NAMES = {
  aruba: "Aruba",
  curacao: "Curaçao",
  bonaire: "Bonaire",
  stmaarten: "St. Maarten",
};

// Trilingual + Papiamento metadata per island. Papiamento variants added
// 2026-02-06 alongside the full-page i18n pass; see /app/memory/
// papiamentu_style.md for the k/c orthography split between `pa` (Curaçao)
// and `pa_aw` (Aruba). Native speaker spot-check pending.
const CRUISE_META = {
  aruba: {
    en: {
      title: "Aruba Cruise Ship Schedule Today — 7-Day Port Calls | IslandsDaily",
      description:
        "Full 7-day Aruba cruise ship schedule. Ship names, cruise lines, arrival and departure times, passenger counts — updated daily from Aruba Ports Authority.",
    },
    nl: {
      title: "Aruba Cruiserooster Vandaag — 7-Daagse Havenaanlopen | IslandsDaily",
      description:
        "Volledig 7-daags cruiserooster voor Aruba. Scheepsnamen, rederijen, aankomst- en vertrektijden, passagiersaantallen — dagelijks bijgewerkt.",
    },
    es: {
      title: "Horario de Cruceros de Aruba Hoy — 7 Días de Escalas | IslandsDaily",
      description:
        "Horario completo de 7 días de cruceros en Aruba. Nombres de barcos, líneas, horarios de llegada y salida, pasajeros — actualizado a diario.",
    },
    pap_aw: {
      title: "Schedule di Crucero na Aruba Awe — 7 Dia di Yegada | IslandsDaily",
      description:
        "Schedule completo di 7 dia di crucero na Aruba. Nomber di barco, compania, ora di yega y salida, pasahero — actualisá tur dia.",
    },
    pap: {
      title: "Skedul di Krusero na Aruba Awe — 7 Dia di Yegada | IslandsDaily",
      description:
        "Skedul kompletu di 7 dia di krusero na Aruba. Nòmber di barku, kompania, ora di yega i sali, pasahero — aktualisá tur dia.",
    },
  },
  curacao: {
    en: {
      title: "Curaçao Cruise Ship Schedule Today — 7-Day Port Calls | IslandsDaily",
      description:
        "Full 7-day Curaçao cruise ship schedule. Ship names, cruise lines, arrival and departure times, passenger counts — updated daily.",
    },
    nl: {
      title: "Curaçao Cruiserooster Vandaag — 7-Daagse Havenaanlopen | IslandsDaily",
      description:
        "Volledig 7-daags cruiserooster voor Curaçao. Scheepsnamen, rederijen, aankomst- en vertrektijden, passagiersaantallen — dagelijks bijgewerkt.",
    },
    es: {
      title: "Horario de Cruceros de Curazao Hoy — 7 Días de Escalas | IslandsDaily",
      description:
        "Horario completo de 7 días de cruceros en Curazao. Nombres de barcos, líneas, horarios de llegada y salida, pasajeros — actualizado a diario.",
    },
    pap: {
      title: "Skedul di Krusero na Kòrsou Awe — 7 Dia di Yegada | IslandsDaily",
      description:
        "Skedul kompletu di 7 dia di krusero na Kòrsou. Nòmber di barku, kompania, ora di yega i sali, pasahero — aktualisá tur dia.",
    },
    pap_aw: {
      title: "Schedule di Crucero na Curaçao Awe — 7 Dia di Yegada | IslandsDaily",
      description:
        "Schedule completo di 7 dia di crucero na Curaçao. Nomber di barco, compania, ora di yega y salida, pasahero — actualisá tur dia.",
    },
  },
  bonaire: {
    en: {
      title: "Bonaire Cruise Ship Schedule Today — 7-Day Port Calls | IslandsDaily",
      description:
        "Full 7-day Bonaire cruise ship schedule. Ship names, cruise lines, arrival and departure times, passenger counts — updated daily.",
    },
    nl: {
      title: "Bonaire Cruiserooster Vandaag — 7-Daagse Havenaanlopen | IslandsDaily",
      description:
        "Volledig 7-daags cruiserooster voor Bonaire. Scheepsnamen, rederijen, aankomst- en vertrektijden, passagiersaantallen — dagelijks bijgewerkt.",
    },
    es: {
      title: "Horario de Cruceros de Bonaire Hoy — 7 Días de Escalas | IslandsDaily",
      description:
        "Horario completo de 7 días de cruceros en Bonaire. Nombres de barcos, líneas, horarios de llegada y salida, pasajeros — actualizado a diario.",
    },
    pap: {
      title: "Skedul di Krusero na Boneiru Awe — 7 Dia di Yegada | IslandsDaily",
      description:
        "Skedul kompletu di 7 dia di krusero na Boneiru. Nòmber di barku, kompania, ora di yega i sali, pasahero — aktualisá tur dia.",
    },
    pap_aw: {
      title: "Schedule di Crucero na Bonaire Awe — 7 Dia di Yegada | IslandsDaily",
      description:
        "Schedule completo di 7 dia di crucero na Bonaire. Nomber di barco, compania, ora di yega y salida, pasahero — actualisá tur dia.",
    },
  },
  stmaarten: {
    en: {
      title: "St. Maarten Cruise Ship Schedule Today — 7-Day Port Calls | IslandsDaily",
      description:
        "Full 7-day St. Maarten cruise ship schedule. Ship names, cruise lines, arrival and departure times, passenger counts — updated daily.",
    },
    nl: {
      title: "St. Maarten Cruiserooster Vandaag — 7-Daagse Havenaanlopen | IslandsDaily",
      description:
        "Volledig 7-daags cruiserooster voor St. Maarten. Scheepsnamen, rederijen, aankomst- en vertrektijden, passagiersaantallen — dagelijks bijgewerkt.",
    },
    es: {
      title: "Horario de Cruceros de San Martín Hoy — 7 Días de Escalas | IslandsDaily",
      description:
        "Horario completo de 7 días de cruceros en San Martín. Nombres de barcos, líneas, horarios de llegada y salida, pasajeros — actualizado a diario.",
    },
    pap: {
      title: "Skedul di Krusero na Sint Maarten Awe — 7 Dia di Yegada | IslandsDaily",
      description:
        "Skedul kompletu di 7 dia di krusero na Sint Maarten. Nòmber di barku, kompania, ora di yega i sali, pasahero — aktualisá tur dia.",
    },
    pap_aw: {
      title: "Schedule di Crucero na Sint Maarten Awe — 7 Dia di Yegada | IslandsDaily",
      description:
        "Schedule completo di 7 dia di crucero na Sint Maarten. Nomber di barco, compania, ora di yega y salida, pasahero — actualisá tur dia.",
    },
  },
};

// Evergreen static context per island — pure prose that adds indexable
// depth around the live schedule table. Written from the port authority
// / cruise-terminal perspective so search-engine snippets surface
// concrete, useful info rather than the sparse "no ships today" state.
// Kept intentionally short: 2-3 sentences each, factual, no dates.
const PORT_CONTEXT = {
  aruba: {
    en: "The Port of Oranjestad is Aruba's only cruise port and one of the busiest in the Caribbean. Ships dock at the Oranjestad Cruise Terminal on the west side of the island, walking distance to downtown shops, restaurants, and the Renaissance Marketplace. The pier can accommodate up to three mid-sized cruise ships simultaneously.",
    nl: "De haven van Oranjestad is de enige cruisehaven van Aruba en een van de drukste in het Caribisch gebied. Schepen meren aan bij de Oranjestad Cruise Terminal aan de westkant van het eiland, op loopafstand van winkels in het centrum, restaurants en de Renaissance Marketplace. De kade kan tot drie middelgrote cruiseschepen tegelijk ontvangen.",
    es: "El Puerto de Oranjestad es el único puerto de cruceros de Aruba y uno de los más concurridos del Caribe. Los barcos atracan en la Terminal de Cruceros de Oranjestad, en el lado oeste de la isla, a poca distancia a pie de las tiendas del centro, restaurantes y el Renaissance Marketplace. El muelle puede recibir hasta tres cruceros de tamaño medio simultáneamente.",
    pap_aw: "E Waf di Oranjestad ta e unico waf di crucero na Aruba y un di esnan mas ocupa den Caribe. Barconan ta ancra na e Terminal di Crucero na Oranjestad na e banda pabou di e isla, cerca di tienda, restaurant y Renaissance Marketplace. E waf por tuma te tres crucero mediano na mesun tempo.",
    pap: "E Haf di Oranjestad ta e uniko haf di krusero na Aruba i un di esnan di mas okupá den Karibe. Barkunan ta ankrá na e Terminal di Krusero na Oranjestad na banda pabou di e isla, serka di tienda, restaurant i Renaissance Marketplace. E haf por tuma te tres krusero mediano na mes tempu.",
  },
  curacao: {
    en: "Curaçao's cruise ships dock in Willemstad at either the Mega Pier (on the Otrobanda side, for the largest ships) or the smaller Mathey Wharf near the historic Punda district. Both are walking distance to the UNESCO-listed downtown, the floating market, and the colorful Handelskade waterfront.",
    nl: "De cruiseschepen van Curaçao meren aan in Willemstad, ofwel bij de Mega Pier (aan de Otrobanda-kant, voor de grootste schepen) ofwel bij de kleinere Mathey Wharf bij het historische Punda-district. Beide liggen op loopafstand van het UNESCO-centrum, de drijvende markt en de kleurrijke Handelskade.",
    es: "Los cruceros de Curazao atracan en Willemstad, ya sea en el Mega Pier (en el lado de Otrobanda, para los barcos más grandes) o en el más pequeño Mathey Wharf cerca del histórico distrito de Punda. Ambos están a poca distancia a pie del centro Patrimonio de la Humanidad, el mercado flotante y el colorido Handelskade.",
    pap: "Krusero di Kòrsou ta ankrá na Willemstad, sea na Mega Pier (banda di Otrobanda, pa e barkunan mas grandi) òf na Mathey Wharf mas chikí serka di distrito históriko di Punda. Tur dos ta serka di sentro UNESCO, e merkado flotante i Handelskade koló.",
    pap_aw: "Crucero di Curaçao ta ancra na Willemstad, sea na Mega Pier (banda di Otrobanda, pa e barconan mas grandi) of na Mathey Wharf mas chikito cerca di distrito historico di Punda. Tur dos ta cerca di sentro UNESCO, e mercado flotante y Handelskade color.",
  },
  bonaire: {
    en: "Bonaire welcomes cruise ships at the Kralendijk cruise terminal in the capital. Smaller ships dock at the South Pier near downtown, while larger vessels use the North Pier. Both piers are within a short walk or shuttle ride to the boulevards, dive shops, and boutique retailers that define Bonaire's compact, walkable center.",
    nl: "Bonaire ontvangt cruiseschepen bij de cruiseterminal van Kralendijk in de hoofdstad. Kleinere schepen meren aan bij de South Pier bij het centrum, terwijl grotere schepen de North Pier gebruiken. Beide kades liggen op korte loop- of shuttleafstand van de boulevards, duikwinkels en boetieks die het compacte, wandelvriendelijke centrum van Bonaire kenmerken.",
    es: "Bonaire recibe a los cruceros en la terminal de Kralendijk, en la capital. Los barcos más pequeños atracan en el Muelle Sur cerca del centro, mientras que los barcos más grandes utilizan el Muelle Norte. Ambos están a poca distancia a pie o en traslado de los bulevares, tiendas de buceo y comercios boutique del compacto centro peatonal de Bonaire.",
    pap: "Boneiru ta risibí krusero na e terminal di krusero na Kralendijk, e kapital. Barkunan mas chikí ta ankrá na Haf Sur serka di sentro, i barkunan mas grandi ta usa Haf Nòrt. Tur dos haf ta serka pa kana òf pa kohe un shuttle bai boulevard, tienda di duik i botik.",
    pap_aw: "Bonaire ta ricibi crucero na e terminal di crucero na Kralendijk, e capital. Barconan mas chikito ta ancra na Waf Sur cerca di sentro, y barconan mas grandi ta usa Waf Nort. Tur dos waf ta cerca pa cana of pa cohe un shuttle bai boulevard, tienda di duik y botik.",
  },
  stmaarten: {
    en: "Sint Maarten's A. C. Wathey Cruise Facility at Great Bay is one of the Caribbean's premier cruise ports, capable of hosting up to six mega-ships simultaneously. From the pier, water taxis run continuously to Front Street in downtown Philipsburg, and taxi vans serve both the Dutch and French sides of the island.",
    nl: "De A. C. Wathey Cruise Facility van Sint Maarten aan Great Bay is een van de belangrijkste cruisehavens van het Caribisch gebied en kan tot zes mega-schepen tegelijk ontvangen. Vanaf de kade varen watertaxi's doorlopend naar Front Street in het centrum van Philipsburg, en taxibusjes bedienen zowel de Nederlandse als de Franse kant van het eiland.",
    es: "La A. C. Wathey Cruise Facility de Sint Maarten en Great Bay es uno de los principales puertos de cruceros del Caribe, capaz de albergar hasta seis megabarcos simultáneamente. Desde el muelle, los taxis acuáticos operan continuamente hacia Front Street en el centro de Philipsburg, y las furgonetas taxi conectan tanto el lado holandés como el francés de la isla.",
    pap: "E A. C. Wathey Cruise Facility di Sint Maarten na Great Bay ta un di e prinsipal haf di krusero na Karibe, kapas di risibí te seis mega-barku na mes tempu. For di e haf, water taxi ta kore kontinuamente pa Front Street den sentro di Philipsburg, i taxi van ta sirbi tantu banda hulandes komo franses di e isla.",
    pap_aw: "E A. C. Wathey Cruise Facility di Sint Maarten na Great Bay ta un di e principal waf di crucero na Caribe, capaz di ricibi te seis mega-barco na mes tempo. For di e waf, water taxi ta core continuamente pa Front Street den sentro di Philipsburg, y taxi van ta sirbi tanto banda hulandes como frances di e isla.",
  },
};

const SITE = "https://islandsdaily.com";

// Read stored language — extended 2026-02-06 to include Papiamento
// variants. Note the app-wide codes are `pap` (Curaçao/Bonaire, k+haf)
// and `pap_aw` (Aruba, c+waf), NOT `pa`/`pa_aw` — those aliases were
// briefly used in a previous cruise-page draft and produced silent
// English fallback because `t(lang, key)` looks up `STRINGS[lang]`.
const readStoredLang = () => {
  try {
    const s = localStorage.getItem("ir_lang");
    if (s === "en" || s === "nl" || s === "es" || s === "pap" || s === "pap_aw") return s;
  } catch (e) { /* ignore */ }
  return "en";
};

export default function CruiseSchedulePage() {
  const { islandSlug } = useParams();
  const slug = URL_TO_SLUG[islandSlug] || null;
  const [data, setData] = useState(null);
  const [ads, setAds] = useState({});
  // Track language reactively so the LanguageToggle in the mini-header
  // actually re-renders the page on change. Previously this was a
  // top-of-render const which never updated after mount — that's why
  // even after `readStoredLang` was fixed, real users clicking the
  // toggle saw no change (the value was captured at first render and
  // frozen). See BUG note in `handleLangChange` for the wiring.
  const [lang, setLang] = useState(() => readStoredLang());
  useEffect(() => {
    const onLangChange = () => setLang(readStoredLang());
    window.addEventListener("ir_lang_changed", onLangChange);
    // Cross-tab support: browser's built-in storage event fires when
    // ir_lang is written from another tab.
    window.addEventListener("storage", onLangChange);
    return () => {
      window.removeEventListener("ir_lang_changed", onLangChange);
      window.removeEventListener("storage", onLangChange);
    };
  }, []);
  const handleLangChange = useCallback((next) => {
    try {
      localStorage.setItem("ir_lang", next);
      window.dispatchEvent(new Event("ir_lang_changed"));
    } catch (e) { /* ignore quota / private-mode */ }
    setLang(next);
  }, []);

  // Trilingual + Papiamento meta driven off the current stored language.
  const seoConfigs = useMemo(() => {
    if (!slug) return {};
    const meta = CRUISE_META[slug] || CRUISE_META.aruba;
    const canonical = `${SITE}/${islandSlug}/cruise-schedule`;
    // Language switch is client-side — all languages live at the same
    // URL, so all hreflang entries point at it.
    const alternates = [
      { hreflang: "en", href: canonical },
      { hreflang: "nl", href: canonical },
      { hreflang: "es", href: canonical },
      { hreflang: "pap", href: canonical },
      { hreflang: "x-default", href: canonical },
    ];
    return {
      en:    { ...meta.en,    canonical, alternates },
      nl:    { ...meta.nl,    canonical, alternates },
      es:    { ...meta.es,    canonical, alternates },
      pap:    { ...(meta.pap    || meta.en), canonical, alternates },
      pap_aw: { ...(meta.pap_aw || meta.en), canonical, alternates },
    };
  }, [slug, islandSlug]);
  useSeoTags(lang, seoConfigs);

  useEffect(() => {
    if (!slug) return;
    api.get("/essentials/cruise/full", { params: { island: slug } })
      .then((r) => setData(r.data))
      .catch(() => setData(null));
    api.get("/ads", { params: { island: slug } })
      .then((r) => setAds(r.data || {}))
      .catch(() => {});
  }, [slug]);

  const entries = data?.entries || [];
  const grouped = useMemo(() => {
    const g = {};
    for (const e of entries) {
      (g[e.date] = g[e.date] || []).push(e);
    }
    return g;
  }, [entries]);
  const sortedDates = Object.keys(grouped).sort();

  // FAQ schema for SEO — built from today's entries
  const faqSchema = useMemo(() => {
    if (!slug) return null;
    const todaysList = grouped[data?.from] || [];
    const total = todaysList.reduce((sum, s) => sum + (s.passengers || 0), 0);
    const shipNames = todaysList.map((s) => s.ship_name).filter(Boolean).join(", ");
    const q1 = {
      "@type": "Question",
      name: `Which cruise ships are in port in ${ISLAND_NAMES[slug]} today?`,
      acceptedAnswer: {
        "@type": "Answer",
        text: todaysList.length
          ? `${shipNames} are in port today in ${ISLAND_NAMES[slug]}. Total passengers: ${total}.`
          : `No cruise ships are in port in ${ISLAND_NAMES[slug]} today.`,
      },
    };
    const first = todaysList[0];
    const q2 = first
      ? {
          "@type": "Question",
          name: `What time do cruise ships arrive in ${ISLAND_NAMES[slug]}?`,
          acceptedAnswer: {
            "@type": "Answer",
            text: `${first.ship_name} arrives at ${first.arrives || "TBD"} and departs at ${first.departs || "TBD"}.`,
          },
        }
      : null;
    return {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: q2 ? [q1, q2] : [q1],
    };
  }, [slug, grouped, data]);

  // ItemList schema for the 7-day schedule (2026-02-06). Google's
  // guidance for tabular schedule data is `ItemList` with a `ListItem`
  // per row — NOT `Event` (which is for happenings the reader can
  // attend). Each list item summarizes the ship + times so a rich-
  // result parser can surface individual arrivals in a carousel.
  const itemListSchema = useMemo(() => {
    if (!slug || entries.length === 0) return null;
    return {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: `${ISLAND_NAMES[slug]} Cruise Ship Schedule — Next 7 Days`,
      numberOfItems: entries.length,
      itemListElement: entries.slice(0, 50).map((e, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: `${e.ship_name} — ${e.cruise_line}`,
        description: [
          `Date: ${e.date}`,
          e.arrives && `Arrives: ${e.arrives}`,
          e.departs && `Departs: ${e.departs}`,
          e.port_label && `Pier: ${e.port_label}`,
          e.passengers && `Passengers: ${e.passengers}`,
        ].filter(Boolean).join(" · "),
      })),
    };
  }, [slug, entries]);

  if (!slug) {
    return (
      <div className="min-h-screen bg-white p-8" data-testid="cruise-page-404">
        <p>{translate(lang, "cruise_unknown_island")}</p>
        <Link to="/" className="text-[#0AADA0]">← {translate(lang, "cruise_back")}</Link>
      </div>
    );
  }

  const meta = ISLAND_BY_SLUG[slug];
  const portContext = PORT_CONTEXT[slug]?.[lang] || PORT_CONTEXT[slug]?.en || "";

  return (
    <div className="min-h-screen bg-white" data-testid={`cruise-schedule-${slug}`}>
      {faqSchema && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
          data-testid="cruise-faq-schema"
        />
      )}
      {itemListSchema && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(itemListSchema) }}
          data-testid="cruise-itemlist-schema"
        />
      )}
      <div className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-[#EEEEEE]">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-3">
          <Link to="/" className="inline-flex items-center gap-1.5 text-[13px] text-[#6B7C82] hover:text-[#1A2E35]" data-testid="cruise-page-back-link">
            <ArrowLeft size={15} /> {translate(lang, "cruise_back")}
          </Link>
          <Link to="/" className="ml-auto font-serif-display text-[18px] leading-none">
            <span className="text-[#1A1A1A]">Islands</span>
            <span className="text-[#0AADA0]">Daily</span>
          </Link>
          {/* Language toggle — 2026-02-06 fix. Without this, users on
              /cruise-schedule pages had no way to switch languages;
              the site's LANGUAGES list didn't include pap/pap_aw
              either, so Papiamento was unreachable end-to-end. */}
          <LanguageToggle lang={lang} onChange={handleLangChange} />
        </div>
      </div>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="mb-6">
          <LeaderboardAd ad={ads.leaderboard} />
        </div>

        <h1 className="font-serif-display text-3xl sm:text-4xl text-[#1A2E35] mb-2" data-testid="cruise-page-h1">
          {meta?.flag} {ISLAND_NAMES[slug]} {translate(lang, "cruise_h1_suffix")}
        </h1>
        <p className="text-[14px] text-[#6B7C82] mb-6" data-testid="cruise-page-subtitle">
          {translate(lang, "cruise_subtitle")}
        </p>

        {sortedDates.length === 0 ? (
          <div className="py-16 text-center text-[#6B7C82]" data-testid="cruise-schedule-empty">
            {translate(lang, "cruise_empty_next_7_days")}
          </div>
        ) : (
          <div className="overflow-x-auto" data-testid="cruise-schedule-table">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="text-[11px] uppercase text-[#6B7C82] tracking-wider border-b border-[#EEEEEE]">
                  <th className="py-2 pr-4">{translate(lang, "cruise_col_date")}</th>
                  <th className="py-2 pr-4">{translate(lang, "cruise_col_ship")}</th>
                  <th className="py-2 pr-4">{translate(lang, "cruise_line")}</th>
                  <th className="py-2 pr-4">{translate(lang, "cruise_col_arrives")}</th>
                  <th className="py-2 pr-4">{translate(lang, "cruise_col_departs")}</th>
                  <th className="py-2 pr-4">{translate(lang, "cruise_col_pier")}</th>
                  <th className="py-2 pr-4">{translate(lang, "cruise_col_passengers")}</th>
                </tr>
              </thead>
              <tbody>
                {sortedDates.map((d) =>
                  grouped[d].map((s, i) => (
                    <tr
                      key={`${d}-${i}`}
                      className="border-b border-[#F5F5F5] text-[13px] text-[#1A2E35]"
                    >
                      <td className="py-2 pr-4 whitespace-nowrap">{d}</td>
                      <td className="py-2 pr-4 font-medium">{s.ship_name}</td>
                      <td className="py-2 pr-4 text-[#6B7C82]">{s.cruise_line}</td>
                      <td className="py-2 pr-4">{s.arrives || "—"}</td>
                      <td className="py-2 pr-4">{s.departs || "—"}</td>
                      <td className="py-2 pr-4 text-[#6B7C82]">{s.port_label || "—"}</td>
                      <td className="py-2 pr-4 text-[#0AADA0]">{s.passengers || "—"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Evergreen port context — factual static prose that adds
            indexable SEO depth around the live table. Kept short and
            reader-useful, no dated info. */}
        {portContext && (
          <section
            className="mt-12 max-w-3xl border-t border-[#EEEEEE] pt-8"
            data-testid="cruise-page-port-context"
          >
            <h2 className="font-serif-display text-xl sm:text-2xl text-[#1A2E35] mb-3">
              {translate(lang, "cruise_about_ports_title")}
            </h2>
            <p className="text-[14px] text-[#3D4C52] leading-[1.7]">
              {portContext}
            </p>
          </section>
        )}
      </main>

      <Footer />
    </div>
  );
}
